import type { IUser } from '@condo/domain';

/**
 * Capitalizes the first letter of a string
 */
const capitalizeString = (str: string | undefined | null): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Interface for user lookup function
 * This should be provided by the useUsers hook
 */
export type UserLookupFunction = (uidOrContactId: string | number | undefined) => IUser | null | undefined;

/**
 * Generate user display name based on user ID
 * Ported exactly from Vue project: /services/core/cockpit/src/shared/helpers.ts
 *
 * @param uidOrContactId - User ID or Contact ID
 * @param getUserData - Function to lookup user data (from useUsers hook)
 * @param withEmail - Include email in parentheses
 * @param withLastName - Include last name
 * @param short - Use short format (initials)
 * @param fallBackName - Fallback name if user not found
 * @returns Formatted user name
 */
export const userName = (
    uidOrContactId: string | number | undefined,
    getUserData: UserLookupFunction,
    withEmail = false,
    withLastName = true,
    short = false,
    fallBackName = 'Unknown User',
): string => {
    if (!uidOrContactId) {
        return fallBackName;
    }

    // Get user data from the lookup function (similar to store.getters.users[uidOrContactId] ?? store.getters.usersByContactId[uidOrContactId])
    const user = getUserData(uidOrContactId);

    const displayName =
        (!short && user?.displayName ? user.displayName : null) ??
        [
            short ? (user?.contact?.firstname ? `${user.contact.firstname[0].toUpperCase()}.` : '') : capitalizeString(user?.contact?.firstname),
            withLastName ? capitalizeString(user?.contact?.lastname) : '',
        ]
            .filter(Boolean)
            .join(' ');

    const email = user?.email;

    if (displayName) {
        return withEmail && email ? `${displayName} (${email})` : displayName;
    }

    if (email) {
        return email;
    }

    // Special handling for GCP service accounts (from original Vue code)
    if (uidOrContactId.toString().includes('@gcp.com')) {
        return 'Condo Platform [automatic]';
    }

    return fallBackName ? fallBackName : !short ? '(unknown)' : '?';
};

/**
 * React Hook version of userName that automatically handles user lookup
 * This is the main function components should use
 */
export const useUserName = (
    uidOrContactId: string | number | undefined,
    getUserData: UserLookupFunction,
    withEmail = false,
    withLastName = true,
    short = false,
    fallBackName = '',
): string => {
    return userName(uidOrContactId, getUserData, withEmail, withLastName, short, fallBackName);
};
